/**
 * Translate between the form's internal shape (``duration: 'short'|'standard'|'long'``)
 * and the backend's ``ViralShortsStartIn`` payload shape (``default_video_duration``
 * in seconds). Kept here so ``ViralShortsForm.tsx`` and ``PresetBar.tsx``
 * use a single source of truth.
 */

import type { components } from '@/../types/api'

/** Backend payload accepts ``thumbnail_style`` (added 2026-04-26 with the
 * thumbnail v2 carousel) and ``stock_source`` (added 2026-05-03 with the
 * Pixabay alternative) but the OpenAPI typegen wasn't re-run yet —
 * augment locally so the form can pass them without a cast. */
export type ViralShortsPayload = components['schemas']['ViralShortsStartIn'] & {
  thumbnail_style?: string
  stock_source?: StockSource
}

export type StockSource = 'pexels' | 'pixabay'

export type DurationKey = 'short' | 'standard' | 'long'

// ``standard`` was 52 s until 07/10/2026. Measured on the viral channels
// (card #295), views fall past 50 s, so it now targets 45 s; 52 still reads
// back as ``standard`` for presets saved before the change.
export const DURATION_SECONDS: Record<DurationKey, number> = {
  short: 30,
  standard: 45,
  long: 75,
}

const SECONDS_TO_KEY: Record<number, DurationKey> = {
  30: 'short',
  45: 'standard',
  52: 'standard',
  75: 'long',
}

/** Form values managed by react-hook-form in ``ViralShortsForm.tsx``. */
export interface FormValues {
  theme: string
  count: number
  language: 'pt-br' | 'en' | 'both'
  duration: DurationKey
  custom_prompt?: string
  end_text?: string
  tone?: 'formal' | 'curious' | 'sarcastic'
  subtitle_style?: string
  subtitle_active_color?: string
  thumbnail_style?: string
  tts_voice_pt?: string
  tts_voice_en?: string
  stock_source?: StockSource
}

const FORM_DEFAULTS: FormValues = {
  theme: 'random',
  count: 1,
  language: 'pt-br',
  duration: 'standard',
  custom_prompt: '',
  end_text: '',
  stock_source: 'pexels',
}

/** Build the backend payload from the form snapshot. ``channel`` is set
 * by the caller because it depends on the active channel context.
 * ``llm_provider`` fica de fora: o toggle Local/Nuvem é decisão por-run,
 * não algo que um preset deva congelar (o backend defaulta pra ``auto``). */
export function formToPayload(values: FormValues): Omit<ViralShortsPayload, 'channel' | 'llm_provider'> {
  const payload: Omit<ViralShortsPayload, 'channel' | 'llm_provider'> = {
    theme: values.theme,
    count: values.count,
    language: values.language,
    default_video_duration: DURATION_SECONDS[values.duration],
    // ``custom_prompt`` defaults to "" on the backend; only carry the real
    // value when the user is in custom-theme mode.
    custom_prompt: values.theme === 'custom' && values.custom_prompt ? values.custom_prompt : '',
  }
  if (values.end_text) payload.end_text = values.end_text
  if (values.tone) payload.tone = values.tone
  if (values.subtitle_style) payload.subtitle_style = values.subtitle_style
  // A cor só vale no karaokê; nos outros estilos ficaria gravada à toa no preset.
  if (values.subtitle_style === 'karaoke' && values.subtitle_active_color) {
    payload.subtitle_active_color = values.subtitle_active_color
  }
  if (values.thumbnail_style) payload.thumbnail_style = values.thumbnail_style
  if (values.tts_voice_pt) payload.tts_voice_pt = values.tts_voice_pt
  if (values.tts_voice_en) payload.tts_voice_en = values.tts_voice_en
  // Only carry stock_source when it differs from the backend default —
  // sending "pexels" explicitly is harmless but keeps presets that predate
  // this field looking the same.
  if (values.stock_source && values.stock_source !== 'pexels') {
    payload.stock_source = values.stock_source
  }
  return payload
}

/** Reverse: backend payload → form values. Used to hydrate the form
 * from a saved preset. Missing keys fall back to the form defaults so
 * the React Hook Form ``reset()`` always receives a complete shape. */
export function payloadToForm(params: Record<string, unknown>): FormValues {
  const seconds = (params.default_video_duration as number | undefined) ?? DURATION_SECONDS.standard
  const duration = SECONDS_TO_KEY[seconds] ?? 'standard'
  const values: FormValues = {
    ...FORM_DEFAULTS,
    theme: (params.theme as string | undefined) ?? FORM_DEFAULTS.theme,
    count: (params.count as number | undefined) ?? FORM_DEFAULTS.count,
    language:
      ((params.language as FormValues['language'] | undefined) ?? null) ?? FORM_DEFAULTS.language,
    duration,
    custom_prompt: (params.custom_prompt as string | undefined) ?? '',
  }
  // exactOptionalPropertyTypes: assign only when present so ``tone?: T`` etc.
  // never receives ``undefined`` explicitly.
  if (params.end_text) values.end_text = params.end_text as string
  if (params.tone) values.tone = params.tone as 'formal' | 'curious' | 'sarcastic'
  if (params.subtitle_style) values.subtitle_style = params.subtitle_style as string
  if (params.subtitle_active_color) values.subtitle_active_color = params.subtitle_active_color as string
  if (params.thumbnail_style) values.thumbnail_style = params.thumbnail_style as string
  if (params.tts_voice_pt) values.tts_voice_pt = params.tts_voice_pt as string
  if (params.tts_voice_en) values.tts_voice_en = params.tts_voice_en as string
  if (params.stock_source === 'pexels' || params.stock_source === 'pixabay') {
    values.stock_source = params.stock_source
  }
  return values
}
